import React from "react";

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div className="mt-10 border-t border-gray-200 pt-6">
    <h2 className="text-2xl font-semibold mb-4 text-gray-800">{title}</h2>
    {children}
  </div>
);

const SubSection: React.FC<SectionProps> = ({ title, children }) => (
  <div className="mt-6">
    <h3 className="text-xl font-semibold mb-2 text-gray-700">{title}</h3>
    {children}
  </div>
);

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto bg-white p-8 shadow-md rounded-2xl">
        <h1 className="text-4xl font-bold mb-6 text-gray-900">Privacy Policy</h1>
        <p className="mb-4 text-sm text-gray-500">Last updated: March 26, 2025</p>
        <p className="mb-4 text-gray-700">
          This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
        </p>
        <p className="mb-4 text-gray-700">
          We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy. This Privacy Policy has been created with the help of the{" "}
          <a
            href="https://www.freeprivacypolicy.com/free-privacy-policy-generator/"
            className="text-blue-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Free Privacy Policy Generator
          </a>.
        </p>

        <Section title="Interpretation and Definitions">
          <SubSection title="Interpretation">
            <p className="mb-4 text-gray-700">
              The words of which the initial letter is capitalized have meanings defined under the following conditions...
            </p>
          </SubSection>
          <SubSection title="Definitions">
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Account:</strong> A unique account created for You to access our Service or parts of our Service.</li>
              <li><strong>Affiliate:</strong> An entity under common control with a party.</li>
              <li><strong>Application:</strong> Refers to OneTicket.</li>
              <li><strong>Company:</strong> SequelString AI Private Limited.</li>
              <li><strong>Country:</strong> Maharashtra, India</li>
              <li><strong>Device:</strong> Any device that can access the Service.</li>
              <li><strong>Personal Data:</strong> Identifiable individual information.</li>
              <li><strong>Service:</strong> The Application.</li>
              <li><strong>Service Provider:</strong> Third party processing data on Our behalf.</li>
              <li><strong>Usage Data:</strong> Automatically collected usage data.</li>
              <li><strong>You:</strong> The individual using the Service.</li>
            </ul>
          </SubSection>
        </Section>

        <Section title="Collecting and Using Your Personal Data">
          <SubSection title="Types of Data Collected">
            <h4 className="text-lg font-semibold mt-4 mb-2 text-gray-700">Personal Data</h4>
            <p className="mb-2 text-gray-700">We may collect the following personal data:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Email address</li>
              <li>First and last name</li>
              <li>Phone number</li>
              <li>Address, ZIP, City</li>
              <li>Usage Data</li>
            </ul>

            <h4 className="text-lg font-semibold mt-6 mb-2 text-gray-700">Usage Data</h4>
            <p className="mb-4 text-gray-700">
              We collect browser/device info, IP address, session data, and other diagnostics.
            </p>

            <h4 className="text-lg font-semibold mt-6 mb-2 text-gray-700">App Data</h4>
            <p className="mb-4 text-gray-700">
              With your permission, we may collect your location to offer you enhanced features.
            </p>
          </SubSection>

          <SubSection title="Use of Your Personal Data">
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>To provide and maintain the Service</li>
              <li>To manage Your Account</li>
              <li>For performance of a contract</li>
              <li>To contact You about updates or offers</li>
              <li>To personalize your experience</li>
              <li>For business transfers and improvements</li>
            </ul>
          </SubSection>

          <SubSection title="Sharing Your Data">
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>With Service Providers</li>
              <li>During business transfers</li>
              <li>With affiliates & partners</li>
              <li>With Your consent</li>
            </ul>
          </SubSection>
        </Section>

        <Section title="Retention, Deletion & Security">
          <p className="mb-4 text-gray-700">
            We retain your data as long as necessary. You can request deletion of your information unless we’re legally required to keep it. We use reasonable measures to protect your data, but cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="Children’s Privacy">
          <p className="mb-4 text-gray-700">
            We do not knowingly collect data from children under 13. Contact us if you believe your child has provided us with personal data.
          </p>
        </Section>

        <Section title="Third-party Links & Updates">
          <p className="mb-4 text-gray-700">
            We may link to external sites not under our control. Review their privacy policies. We'll notify you of major changes to this policy via email or app notification.
          </p>
        </Section>

        <Section title="Contact Us">
          <p className="text-gray-700">For any questions, reach out via email:</p>
          <p className="text-blue-600 mt-2">info@sequelstring.com</p>
        </Section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
